import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

// Las 14 preguntas FAQ con contenido SEO completo
const faqData = [
  {
    id: 'faq-1',
    question: '¿Qué es la alimentación natural para perros y gatos?',
    answer: 'Es una dieta basada en alimentos frescos y poco procesados: carnes, pescados, vísceras, huesos carnosos adecuados, verduras y los suplementos necesarios para que sea completa. No consiste en dar comida de casa, sino en formular un menú equilibrado según las necesidades de cada animal.',
    defaultOpen: true,
  },
  {
    id: 'faq-2',
    question: '¿Una dieta natural casera es segura y equilibrada?',
    answer: 'Lo es cuando la formula un profesional. Una dieta casera improvisada puede generar carencias o excesos, sobre todo de calcio, fósforo y vitaminas. Por eso cada plan se calcula para tu animal, con cantidades exactas y los suplementos necesarios.',
    defaultOpen: true,
  },
  {
    id: 'faq-3',
    question: '¿Cómo funciona la consulta online?',
    answer: 'Una vez agendes la consulta, te enviamos un formulario detallado que rellenas sobre tu animal: edad, peso, historial clínico, hábitos y alimentación actual. Después nos conectamos por videollamada para revisar el caso en profundidad. Todo se hace desde casa, sin desplazamientos ni estrés para tu animal.',
    defaultOpen: true,
  },
  {
    id: 'faq-4',
    question: '¿Cuánto cuesta?',
    answer: 'La valoración nutricional inicial cuesta 35 €, y si después contratas el pack nutricional específico, esos 35 € se descuentan de su precio. El pack nutricional específico cuesta 100 €. Cada consulta de seguimiento cuesta 35 €.',
    defaultOpen: false,
  },
  {
    id: 'faq-5',
    question: '¿Qué incluye el pack nutricional específico?',
    answer: 'El pack incluye: valoración nutricional inicial completa, plan nutricional personalizado con ingredientes y cantidades exactas, guía de preparación, y pautas de transición día a día para que el cambio sea seguro. También incluye recomendaciones de suplementos si son necesarios para tu caso. Todo diseñado para tu animal, no genérico.',
    defaultOpen: false,
  },
  {
    id: 'faq-6',
    question: '¿Necesito experiencia previa con alimentación natural?',
    answer: 'No. Te acompaño desde cero con instrucciones claras: qué comprar, cómo prepararlo, cómo conservarlo y cómo servirlo. Lo único que necesitas es constancia.',
    defaultOpen: false,
  },
  {
    id: 'faq-7',
    question: '¿Es adecuado para perros o gatos con enfermedades?',
    answer: 'Sí. Cada caso se evalúa de forma individual y, cuando hay una patología (renal, digestiva, dermatológica, hepática, articular, sobrepeso...), el plan se ajusta a ella. La alimentación complementa el tratamiento de tu veterinario habitual, no lo sustituye.',
    defaultOpen: false,
  },
  {
    id: 'faq-8',
    question: '¿Puedo hacer la consulta si mi animal está en tratamiento veterinario?',
    answer: 'Sí. De hecho, es importante saber qué tratamientos está recibiendo para adaptar la dieta y evitar interferencias.',
    defaultOpen: false,
  },
  {
    id: 'faq-9',
    question: '¿Se puede combinar alimentación natural con pienso?',
    answer: 'Depende del caso y del objetivo. En algunos animales es viable hacer una transición mixta o mantener una combinación a largo plazo. En la valoración analizamos qué tiene más sentido según su situación, estilo de vida, y tus posibilidades.',
    defaultOpen: false,
  },
  {
    id: 'faq-10',
    question: '¿Sirve para cachorros, gatitos y animales senior?',
    answer: 'Sí. Precisamente estas etapas de vida requieren una atención especial a los nutrientes, cantidades y proporciones. Cada plan se adapta a la edad, condición física y necesidades particulares de tu animal.',
    defaultOpen: false,
  },
  {
    id: 'faq-11',
    question: '¿Atiendes a cualquier raza, tamaño o tipo de animal?',
    answer: 'Sí. Trabajo con perros de razas pequeñas, medianas y grandes, y con gatos de cualquier edad y condición. Cada plan es completamente individualizado.',
    defaultOpen: false,
  },
  {
    id: 'faq-12',
    question: '¿Cuánto tarda en notarse el cambio?',
    answer: 'Depende del animal y de su punto de partida. Cambios como la calidad de las heces, la energía o el apetito pueden notarse en pocos días. Otros, como la piel, el pelo o el peso, requieren semanas de constancia.',
    defaultOpen: false,
  },
  {
    id: 'faq-13',
    question: '¿Cómo funciona el seguimiento?',
    answer: 'El seguimiento se realiza mediante la reserva de una consulta. En ella revisamos cómo ha evolucionado tu animal y ajustamos cantidades, ingredientes o pautas según lo que necesite, para que cada revisión tenga su espacio y toda mi atención.',
    defaultOpen: false,
  },
  {
    id: 'faq-14',
    question: '¿Atiendes solo en Málaga?',
    answer: 'No. Mi base está en Málaga, pero trabajo online con clientes de toda España. La consulta es por videollamada y toda la documentación se entrega de forma digital. No importa dónde estés, puedo ayudarte igual.',
    defaultOpen: false,
  },
  {
    id: 'faq-15',
    question: '¿Qué pasa si tengo que mover o cancelar una cita?',
    answer: 'A todos nos surgen imprevistos así que ¡no te preocupes! Agradecemos que me avises lo antes posible para poder avisar a otra familia que esté esperando a tener cita. Por ello si vas a necesitar cancelar o mover la sesión, deberás hacerlo con un margen de 1 día laboral antes. En el caso de no ponerse en contacto o avisar con dicho margen, no se devolverá el importe de dicha cita.',
    defaultOpen: false,
  },
];

// Schema.org FAQPage para SEO
export const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqData.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

export const FAQSeoSection = () => {
  // Las primeras 3 preguntas abiertas por defecto
  const defaultOpenItems = faqData
    .filter(faq => faq.defaultOpen)
    .map(faq => faq.id);

  return (
    <section 
      className="py-20 md:py-28 bg-white" 
      id="faq"
      data-testid="faq-seo-section"
      aria-labelledby="faq-heading"
    >
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 
            id="faq-heading"
            className="font-manrope text-3xl md:text-4xl lg:text-5xl font-bold text-text-primary tracking-tight mb-4"
          >
            Preguntas frecuentes sobre nutrición natural para perros y gatos
          </h2>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Todo lo que necesitas saber antes de dar el paso hacia una alimentación más saludable.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion 
            type="multiple" 
            defaultValue={defaultOpenItems}
            className="space-y-4"
          >
            {faqData.map((faq, index) => (
              <AccordionItem 
                key={faq.id} 
                value={faq.id}
                className="bg-[#F8FAF7] rounded-2xl border border-brand-sage/20 px-6 overflow-hidden"
                data-testid={`faq-item-${index + 1}`}
              >
                <AccordionTrigger className="py-5 hover:no-underline text-left">
                  <span className="font-manrope font-semibold text-text-primary text-base md:text-lg pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-5" forceMount>
                  {/* El contenido está siempre en el DOM para SEO */}
                  <p className="text-text-secondary leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* CTA después de las FAQ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-center mt-12"
        >
          <Link
            to="/como-funciona"
            className="btn-primary inline-flex items-center justify-center gap-2 group"
            data-testid="faq-cta"
          >
            Reserva tu valoración nutricional inicial
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSeoSection;
